import { useMemo } from 'react';
import { useCopyToClipboard } from 'react-use';
import { twMerge } from 'tailwind-merge';

import { AmaEvent, UserStatusApiResponse } from '../types/api.types';

import { DEFAULTS } from 'constants/defaults.constant';

import introSectionStyles from '../../singleEvent/components/IntroSection/IntroSection.module.scss';
import Button from 'components/buttons/Button';
import Countdown from 'components/countdown/Countdown';
import { ReferralCountdownPart } from 'components/countdown/ReferralCountdownPart';
import { GlassBox } from 'components/glassBox/GlassBox';

import { useAmaCodeVerificationState } from '../hooks/useAmaCodeVerificationState';
import { useIsMobile } from 'hooks/useScreenSize';

import appraisementIcon from 'assets/single-event/appraisement.svg';
import rewardsIcon from 'assets/single-event/rewards.svg';

import { formatDateTime } from '../helpers/date-format.helper';
import { CodeVerificationInput } from './CodeVerificationInput';

type JoinAmaProps = {
  eventId: string;
  amaEvent: AmaEvent | null;
  userStatus: UserStatusApiResponse | undefined;
  registrationEndDatetime: string;
  startDatetime: string;
};

export const JoinAma = ({ eventId, amaEvent, userStatus, registrationEndDatetime, startDatetime }: JoinAmaProps) => {
  const [clipboardState, copyToClipboard] = useCopyToClipboard();
  const isMobile = useIsMobile();

  const { shouldShowCodeInput, shouldShowCountdown, countdownTarget } = useAmaCodeVerificationState({
    amaEvent,
    userStatus,
    startDatetime,
  });

  const amaLink = amaEvent?.ama_link || '';
  const isCodeVerified = userStatus?.ama_verification?.status === 'VERIFIED';

  const onClickCopyJoinAmaLink = () => {
    copyToClipboard(amaLink);
  };

  const valueClass = isMobile
    ? 'tw-text-[42px] tw-font-bold tw-min-w-[60px] tw-h-[60px] tw-px-2'
    : 'tw-text-[42px] tw-font-bold tw-w-[60px] tw-h-[60px]';

  const formattedAmaStartDate = useMemo(() => formatDateTime(startDatetime), [startDatetime]);
  const formattedRegistrationEndDate = useMemo(
    () => formatDateTime(registrationEndDatetime),
    [registrationEndDatetime]
  );

  return (
    <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-[20px] tw-px-[20px] lg:tw-px-0">
      <h2 className="tw-font-jost tw-text-[40px] tw-font-bold tw-leading-[120%] tw-text-white">
        Join Our Exclusive AMA
      </h2>
      <div className="tw-font-jost tw-text-[12px] tw-leading-[20px] tw-text-white lg:tw-text-[15px]">
        Happening on {formattedAmaStartDate} — join using the link below:
      </div>

      <GlassBox
        gradient="linear-gradient(to bottom right, #2D5EFF 0%, rgba(45, 94, 255, 0.2) 100%)"
        borderWidth="1px"
        className="tw-flex tw-w-full tw-flex-row tw-items-center tw-justify-center tw-gap-[20px] tw-p-[10px] lg:tw-max-w-[550px]"
      >
        <span className="tw-min-w-0 tw-flex-1 tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-text-left tw-text-[15px] tw-font-bold tw-text-[#20AA67]">
          {amaLink}
        </span>
        <Button
          text={clipboardState.value ? 'Copied!' : 'Copy'}
          size="medium-small"
          color="referral-cta"
          className="tw-w-[120px] tw-min-w-[120px] lg:tw-w-[120px] lg:tw-min-w-[100px]"
          contentClassName="tw-text-[15px] lg:tw-text-[18px] tw-normal-case tw-font-semibold"
          onClick={onClickCopyJoinAmaLink}
        />
      </GlassBox>

      <div className="tw-flex tw-w-full tw-flex-col tw-items-center tw-justify-center tw-gap-[20px] lg:tw-flex-row">
        {isCodeVerified && (
          <GlassBox
            gradient="linear-gradient(to bottom right, #2D5EFF 0%, rgba(45, 94, 255, 0.2) 100%)"
            borderWidth="1px"
            className="tw-flex tw-w-full tw-w-full tw-flex-col tw-items-center tw-justify-center tw-gap-[10px] tw-p-[25px]"
          >
            <img src={appraisementIcon} alt="Ama Prize" className="tw-h-[100px] tw-w-[100px]" />
            <span className="tw-font-jost tw-text-[20px] tw-font-bold tw-uppercase tw-leading-[20px] tw-text-[#1FB563]">
              Appraisement:
            </span>
            <p className="tw-p-0 tw-font-jost tw-text-[20px] tw-font-bold tw-leading-[24px] tw-text-white">
              Congratulations! <br /> The Code You Entered Is Correct!
            </p>
          </GlassBox>
        )}

        <GlassBox
          gradient="linear-gradient(to bottom right, #2D5EFF 0%, rgba(45, 94, 255, 0.2) 100%)"
          borderWidth="1px"
          className={twMerge(
            'tw-flex tw-w-full tw-flex-col tw-items-center tw-justify-center tw-gap-[10px] tw-p-[25px]',
            !isCodeVerified && 'lg:tw-max-w-[550px]'
          )}
        >
          <img src={rewardsIcon} alt="Ama Prize" className="tw-h-[100px] tw-w-[100px]" />
          <span className="tw-font-jost tw-text-[20px] tw-font-bold tw-uppercase tw-leading-[20px] tw-text-[#1FB563]">
            Rewards:
          </span>
          <p className="tw-m-0 tw-font-jost tw-text-[20px] tw-font-semibold tw-leading-[20px] tw-text-white">
            {amaEvent?.ama_prize?.name || 'Prize'}
          </p>
          <p className="tw-p-0 tw-font-jost tw-text-[15px] tw-leading-[20px] tw-text-white">
            *available only to users who registered before {formattedRegistrationEndDate}
          </p>
        </GlassBox>
      </div>

      {shouldShowCountdown && countdownTarget && (
        <Countdown
          label="Reward Claim Opens In:"
          endTimestamp={countdownTarget * DEFAULTS.tradeTimestampMultiplier}
          labelClass={['tw-text-white tw-text-[20px] tw-font-bold tw-leading-[24px] tw-mb-[16px] tw-capitalize']}
          valueClass={[valueClass, '!tw-text-[36px] tw-font-bold']}
          containerClass={[introSectionStyles['intro-section-countdown-part-container']]}
          labelsClass={['tw-uppercase tw-font-medium tw-text-[12px] lg:tw-text-[15px] tw-mt-[8px] tw-text-[#20AA67]']}
          valueWrapperClass={['tw-w-[62px] tw-h-[62px]']}
          CountdownPartComponent={ReferralCountdownPart}
        />
      )}

      {shouldShowCodeInput && <CodeVerificationInput eventId={eventId} />}
    </div>
  );
};
